// Dialogue box: a speaker's name plate, a portrait well and typewriter text. Click / Enter / Space
// finishes the line early; `onDone` fires when the text is fully shown. Reduced motion shows the
// whole line at once.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { motionReduced } from './Motion';
import { playSfx } from '../../core/audio';

/** The key's level in the story cinematics (the intro, the face-off, the getaway): under the words, not over them. */
export const CINE_TYPE_GAIN = 0.3;

/** `typeGain` scales the soft key under the typing (1 = the bank's level). */
export function useTypewriter(text: string, cps = 55, typeGain = 1): { shown: string; done: boolean; skip: () => void } {
  const [n, setN] = useState(() => (motionReduced() ? text.length : 0));
  useEffect(() => {
    if (motionReduced()) { setN(text.length); return; }
    setN(0);
    // by the clock, not by ticks: a busy frame (the board's lighting, a slow GPU) delays the timer
    // but never slows the line down
    const t0 = performance.now();
    const ms = Math.max(8, 1000 / cps);
    const t = setInterval(() => {
      const k = Math.min(text.length, Math.floor(((performance.now() - t0) * cps) / 1000) + 1);
      setN((prev) => {
        // docs/34: a soft key every few letters while the line writes itself
        if (k > prev && Math.floor(k / 3) > Math.floor(prev / 3) && text[k - 1] !== ' ') playSfx('ui.type', { gain: typeGain });
        return Math.max(prev, k);
      });
      if (k >= text.length) clearInterval(t);
    }, ms);
    return () => clearInterval(t);
  }, [text, cps, typeGain]);
  const skip = useCallback(() => setN(text.length), [text]);
  return { shown: text.slice(0, n), done: n >= text.length, skip };
}

export function DialogueBox({ speaker, text, portrait, aside, onDone, onAdvance, onType, more, className = '', cps, typeGain, ...rest }: {
  speaker?: ReactNode; text: string; portrait?: ReactNode; /** buttons under the line */ aside?: ReactNode;
  onDone?: () => void; /** called on click/Enter once the line is complete (shows the ▼ prompt) */ onAdvance?: () => void;
  /** characters shown so far, as the line types (docs/43 story beats) */ onType?: (n: number) => void;
  more?: boolean; className?: string; cps?: number; /** the typing key's level (CINE_TYPE_GAIN in cinematics) */ typeGain?: number; 'data-testid'?: string;
}) {
  const { shown, done, skip } = useTypewriter(text, cps, typeGain);
  useEffect(() => { if (done) onDone?.(); }, [done, onDone]);
  useEffect(() => { onType?.(shown.length); }, [shown.length, onType]);
  const advance = () => { if (!done) skip(); else onAdvance?.(); };
  const clickable = !done || !!onAdvance;
  return (
    <div className={`fk-panel fk-panel--ornate fk-hit fk-dialogue ${portrait ? '' : 'fk-dialogue--no-portrait'} ${clickable ? 'fk-dialogue--clickable' : ''} ${className}`}
      onClick={clickable ? advance : undefined} onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); advance(); } } : undefined} tabIndex={clickable ? 0 : undefined}
      role={clickable ? 'button' : 'group'} aria-label={typeof speaker === 'string' ? `${speaker} says` : 'Dialogue'} data-done={done} {...rest}>
      {speaker && <span className="fk-dialogue__name">{speaker}</span>}
      {portrait && <div className="fk-dialogue__portrait" aria-hidden="true">{portrait}</div>}
      <p className="fk-dialogue__text" aria-live="polite">{shown}{!done && <span className="fk-dialogue__cursor" aria-hidden="true">▌</span>}</p>
      {aside && <div className="fk-dialogue__aside" onClick={(e) => e.stopPropagation()}>{aside}</div>}
      {done && (more || onAdvance) && <span className="fk-dialogue__more" aria-hidden="true">▼</span>}
    </div>
  );
}
